import React, { useEffect, useState } from "react";
import Select from "react-select";
import validator from "validator";
import {
  Button,
  Card,
  Form,
  Container,
  Row,
  Col,
  Table,
  Badge,
  Alert,
} from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import {
  addSaleInvoice,
  fetchSaleInvoices,
} from "../../../../../Redux/saleInvoiceSlice";
import { useInvoiceData, notify } from "../hooks/useInvoiceData";

function AddDeliveryNote() {
  const {
    date,
    setDate,
    invoiceItems,
    setInvoiceItems,
    invoiceNumber,
    setInvoiceNumber,
    products,
    clients,
    drivers,
    selectedCities,
    setSelectedCities,
    client,
    setClient,
    driver,
    setDriver,
    totalHT,
    setTotalHT,
    totalTTC,
    setTotalTTC,
    totalVAT,
    setTotalVAT,
    type,
    setType,
    status,
    setStatus,
    shippingNoteInvoices,
    setShippingNoteInvoices,
    selectedShippingNote,
    setSelectedShippingNote,
    shippingNoteProducts,
    setShippingNoteProducts,
    loadingShippingNotes,
    setLoadingShippingNotes,

    id,
    dispatch,
    navigate,
    calculateTotals,
  } = useInvoiceData("DELIVERY_NOTE", false);

  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(null);
  const [filteredDrivers, setFilteredDrivers] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  // Filter drivers for commercial users
  useEffect(() => {
    if (currentUser && currentUser.role === "COMMERCIAL" && currentUser.cin) {
      // Filter drivers that belong to this commercial
      const filtered = drivers.filter(
        (driver) => driver.cin === currentUser.cin,
      );
      setFilteredDrivers(filtered);

      // If only one driver, auto-select them
      if (filtered.length === 1 && !driver) {
        const singleDriver = filtered[0];
        setDriver({
          value: singleDriver.id,
          label: `${singleDriver.firstName} ${singleDriver.lastName}`,
          driver: singleDriver,
          cin: singleDriver.cin,
        });
      }
    } else {
      setFilteredDrivers(drivers);
    }
  }, [currentUser, drivers]);

  // Fetch shipping note invoices when driver is selected
  useEffect(() => {
    const fetchShippingNoteInvoices = async () => {
      if (driver?.value) {
        setLoadingShippingNotes(true);
        try {
          let filters = { type: "SHIPPING_NOTE_INVOICE" };

          // If user is COMMERCIAL, filter by the commercial's CIN
          if (currentUser && currentUser.role === "COMMERCIAL") {
            if (currentUser.cin) {
              filters.driverCIN = currentUser.cin;
            }
          }

          const response = await dispatch(fetchSaleInvoices(filters));
          let filteredInvoices = response.payload || [];

          // Apply driver filter
          filteredInvoices = filteredInvoices.filter(
            (inv) => inv.driver?.id === driver.value,
          );

          setShippingNoteInvoices(filteredInvoices);

          // Auto-select if only one shipping note is available and no note selected
          if (filteredInvoices.length === 1 && !selectedShippingNote) {
            const singleNote = filteredInvoices[0];
            setSelectedShippingNote({
              value: singleNote.id,
              label: singleNote.invoiceNumber,
            });
            loadShippingNoteData(singleNote);
          }
        } catch (error) {
          console.error("Error fetching shipping notes:", error);
          notify(2, "Erreur lors du chargement des factures bon sortie");
          setShippingNoteInvoices([]);
        } finally {
          setLoadingShippingNotes(false);
        }
      } else {
        setShippingNoteInvoices([]);
        setSelectedShippingNote(null);
        setShippingNoteProducts([]);
      }
    };

    fetchShippingNoteInvoices();
  }, [driver, dispatch, currentUser]);

  // Recalculate when shipping note products are loaded
  useEffect(() => {
    if (shippingNoteProducts.length > 0) {
      calculateTotals();
    }
  }, [shippingNoteProducts, calculateTotals]);

  const handleItemChange = (index, field, value, additionalData = {}) => {
    const newItems = invoiceItems.map((item, i) => {
      if (i === index) {
        let updatedItem = { ...item, [field]: value };

        if (field === "productId" && Object.keys(additionalData).length > 0) {
          updatedItem = {
            ...updatedItem,
            price: additionalData.price || 0,
            vatRate: additionalData.vatRate || 0,
            originalQuantity: additionalData.originalQuantity || null,
            quantity: 1,
            shippingNoteItemId:
              additionalData.shippingNoteItemId ||
              item.shippingNoteItemId ||
              null,
          };
        } else if (
          field === "productId" &&
          Object.keys(additionalData).length === 0
        ) {
          const selectedProduct = products.find((p) => p.id === value);
          if (selectedProduct) {
            updatedItem.price =
              selectedProduct.salePrice || selectedProduct.price || 0;
            updatedItem.vatRate = selectedProduct.vatRate || 0;
            updatedItem.originalQuantity = null;
            updatedItem.shippingNoteItemId = null;
          }
        }

        if (field === "quantity" && updatedItem.originalQuantity) {
          if (value > updatedItem.originalQuantity) {
            notify(
              2,
              `La quantité ne peut pas dépasser ${updatedItem.originalQuantity}`,
            );
            updatedItem.quantity = updatedItem.originalQuantity;
          }
        }

        const itemHT = (updatedItem.price || 0) * (updatedItem.quantity || 0);
        const itemVAT = itemHT * ((updatedItem.vatRate || 0) / 100);

        updatedItem.totalHT = itemHT;
        updatedItem.vatAmount = itemVAT;
        updatedItem.totalTTC = itemHT + itemVAT;

        return updatedItem;
      }
      return item;
    });

    setInvoiceItems(newItems);
  };

  const handleAddItem = () => {
    if (selectedShippingNote && shippingNoteProducts.length > 0) {
      setInvoiceItems([
        ...invoiceItems,
        {
          productId: "",
          quantity: 1,
          price: 0,
          vatRate: 0,
          vatAmount: 0,
          totalHT: 0,
          totalTTC: 0,
          originalQuantity: null,
          shippingNoteItemId: null,
        },
      ]);
    } else if (selectedShippingNote && shippingNoteProducts.length === 0) {
      notify(2, "Aucun produit disponible dans cette facture bon sortie");
    } else {
      setInvoiceItems([
        ...invoiceItems,
        {
          productId: "",
          quantity: 1,
          price: 0,
          vatRate: 0,
          vatAmount: 0,
          totalHT: 0,
          totalTTC: 0,
          shippingNoteItemId: null,
        },
      ]);
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(newItems);
  };

  const loadShippingNoteData = (shippingNote) => {
    if (
      window.confirm(
        "Voulez-vous charger les données de cette facture bon sortie ?",
      )
    ) {
      setInvoiceItems([]);
      setDate(shippingNote.date.split("T")[0]);

      if (shippingNote.client) {
        setClient({
          value: shippingNote.client.id,
          label: shippingNote.client.name,
          client: shippingNote.client,
        });
      }

      if (shippingNote.cities && shippingNote.cities.length > 0) {
        const citiesFromNote = shippingNote.cities.map((cityRelation) => ({
          value: cityRelation.city.id,
          label: cityRelation.city.name,
          state: cityRelation.city.state,
          country: cityRelation.city.country,
        }));
        setSelectedCities(citiesFromNote);
      }

      const allProducts = shippingNote.items.map((item) => {
        const product = products.find((p) => p.id === item.productId);
        return {
          productId: item.productId,
          productName: product?.name || `Produit ${item.productId}`,
          quantity: item.quantity,
          price: item.price,
          vatRate: item.vatRate,
          vatAmount: item.vatAmount,
          originalQuantity: item.quantity,
          shippingNoteItemId: item.id,
        };
      });

      setShippingNoteProducts(allProducts);

      const availableProducts = allProducts.filter(
        (p) => p.quantity > 0,
      ).length;
      if (availableProducts > 0) {
        notify(
          1,
          `${availableProducts} produit(s) disponible(s) dans cette facture bon sortie`,
        );
      } else {
        notify(2, "Aucun produit disponible dans cette facture bon sortie");
      }
    }
  };

  const getProductOptions = () => {
    if (selectedShippingNote && shippingNoteProducts.length > 0) {
      return shippingNoteProducts
        .filter((p) => p.quantity > 0)
        .map((p) => ({
          label: `${p.productName} (Dispo: ${p.quantity})`,
          value: p.productId,
          originalQuantity: p.quantity,
          price: p.price,
          vatRate: p.vatRate,
          shippingNoteItemId: p.shippingNoteItemId,
        }));
    } else {
      return products.map((product) => ({
        label: product.name,
        value: product.id,
        price: product.salePrice || product.price || 0,
        vatRate: product.vat || 0,
      }));
    }
  };

  const shouldShowOriginalQuantity = () => {
    return selectedShippingNote && shippingNoteProducts.length > 0;
  };

  const submitForm = async () => {
    if (validator.isEmpty(invoiceNumber)) {
      notify(2, "Numéro de facture est obligatoire");
      return;
    }

    if (!client) {
      notify(2, "Client est obligatoire");
      return;
    }

    if (driver && !selectedShippingNote) {
      notify(2, "Veuillez sélectionner une facture bon sortie");
      return;
    }

    if (invoiceItems.length === 0) {
      notify(2, "Au moins un article est obligatoire");
      return;
    }

    for (let i = 0; i < invoiceItems.length; i++) {
      if (!invoiceItems[i].productId) {
        notify(2, `L'article ${i + 1} doit avoir un produit sélectionné`);
        return;
      }

      if (
        invoiceItems[i].originalQuantity &&
        invoiceItems[i].quantity > invoiceItems[i].originalQuantity
      ) {
        notify(
          2,
          `La quantité de l'article ${i + 1} ne peut pas dépasser ${invoiceItems[i].originalQuantity}`,
        );
        return;
      }
    }

    const items = invoiceItems.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
      price: item.price,
      vatRate: item.vatRate,
      vatAmount: item.vatAmount,
      shippingNoteItemId: item.shippingNoteItemId || null,
    }));

    try {
      const invoiceData = {
        invoiceNumber,
        date,
        type: "DELIVERY_NOTE",
        status,
        items,
        totalHT,
        totalTTC,
        clientId: client.value,
      };

      if (driver) {
        invoiceData.driverId = driver.value;
      }

      if (selectedShippingNote) {
        invoiceData.shippingNoteId = selectedShippingNote.value;
      }

      await dispatch(addSaleInvoice(invoiceData));
      notify(1, "Bon de livraison ajouté avec succès");

      setTimeout(
        () => navigate.push("/sale-invoices/list/DELIVERY_NOTE"),
        1500,
      );
    } catch (error) {
      console.error("Error saving delivery note:", error);
      notify(2, "Erreur lors de l'enregistrement");
    }
  };

  const statusOptions = [
    { value: "DRAFT", label: "Brouillon" },
    { value: "VALIDATED", label: "Validée" },
    { value: "PAID", label: "Payée" },
    { value: "CANCELLED", label: "Annulée" },
  ];

  const driverOptions = (
    currentUser?.role === "COMMERCIAL" ? filteredDrivers : drivers
  ).map((driver) => ({
    label: `${driver.firstName} ${driver.lastName} ${driver.phone ? `(${driver.phone})` : ""} ${driver.cin ? `- CIN: ${driver.cin}` : ""}`,
    value: driver.id,
    driver: driver,
    cin: driver.cin,
  }));

  return (
    <Container fluid>
      <ToastContainer />
      <Row>
        <Col md="12">
          <Button
            className="btn-wd btn-outline mr-1 float-left"
            variant="info"
            onClick={() => navigate.push("/sale-invoices/list/DELIVERY_NOTE")}
          >
            <span className="btn-label">
              <i className="fas fa-list"></i>
            </span>
            Retour à la liste
          </Button>
        </Col>
      </Row>

      <Form className="form">
        <Card>
          <Card.Header>
            <Card.Title as="h4">Ajouter bon de livraison</Card.Title>
            {currentUser?.role === "COMMERCIAL" && (
              <Badge bg="info" className="ml-2">
                Commercial: {currentUser.cin}
              </Badge>
            )}
          </Card.Header>

          <Card.Body>
            <Row>
              <Col md="4">
                <Form.Group>
                  <label>Numéro de facture* </label>
                  <Form.Control
                    value={invoiceNumber}
                    placeholder="Ex: BL-2023-001"
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group>
                  <label>Type de facture* </label>
                  <Form.Control value="Bon de livraison" readOnly />
                </Form.Group>
              </Col>
              <Col md="4">
                <Form.Group>
                  <label>Statut </label>
                  <Select
                    value={statusOptions.find((opt) => opt.value === status)}
                    options={statusOptions}
                    onChange={(e) => setStatus(e.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md="4">
                <Form.Group>
                  <label>Date* </label>
                  <Form.Control
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md="4">
                <Form.Group>
                  <label>Client* </label>
                  <Select
                    placeholder="Sélectionner un client"
                    value={client}
                    options={clients.map((client) => ({
                      label: client.name,
                      value: client.id,
                      client: client,
                    }))}
                    onChange={(e) => setClient(e)}
                  />
                </Form.Group>
              </Col>

              <Col md="4">
                <Form.Group>
                  <label>Chauffeur (optionnel)</label>
                  {currentUser?.role === "COMMERCIAL" ? (
                    // Show read-only input with driver's full name for commercial users
                    <Form.Control
                      type="text"
                      value={
                        driver
                          ? `${driver.driver?.firstName} ${driver.driver?.lastName}`
                          : "Aucun chauffeur sélectionné"
                      }
                      readOnly
                      disabled
                      style={{ backgroundColor: "#e9ecef" }}
                    />
                  ) : (
                    // Show select for non-commercial users
                    <Select
                      placeholder="Sélectionner un chauffeur"
                      value={driver}
                      options={driverOptions}
                      onChange={(e) => setDriver(e)}
                      isClearable
                    />
                  )}
                  <small className="text-muted">
                    {currentUser?.role === "COMMERCIAL"
                      ? "Chauffeur automatiquement associé à votre compte commercial"
                      : "Sélectionnez pour lier à une facture bon sortie"}
                  </small>
                </Form.Group>
              </Col>
            </Row>

            {/* Shipping Note Invoices List */}
            {driver?.value && (
              <Row className="mt-3">
                <Col md="12">
                  <Card className="bg-light">
                    <Card.Header>
                      <Card.Title as="h6">
                        <i className="fas fa-file-invoice mr-2"></i>
                        Factures bon sortie disponibles pour ce chauffeur
                        {loadingShippingNotes && (
                          <span className="ml-2">
                            <i className="fas fa-spinner fa-spin"></i>
                          </span>
                        )}
                        <Badge bg="info" className="ml-2">
                          {shippingNoteInvoices.length}
                        </Badge>
                      </Card.Title>
                    </Card.Header>
                    <Card.Body
                      style={{ maxHeight: "250px", overflowY: "auto" }}
                    >
                      {shippingNoteInvoices.length > 0 ? (
                        <Table size="sm" striped bordered hover>
                          <thead>
                            <tr>
                              <th>N° Facture</th>
                              <th>Date</th>
                              <th>Client</th>
                              <th>Chauffeur</th>
                              <th>CIN Chauffeur</th>
                              <th>Villes</th>
                              <th>Total TTC</th>
                              <th>Produits disponibles</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {shippingNoteInvoices.map((note) => {
                              const availableProducts =
                                note.items?.filter((item) => item.quantity > 0)
                                  .length || 0;

                              return (
                                <tr key={note.id}>
                                  <td>{note.invoiceNumber}</td>
                                  <td>
                                    {new Date(note.date).toLocaleDateString()}
                                  </td>
                                  <td>{note.client?.name || "N/A"}</td>
                                  <td>
                                    {note.driver
                                      ? `${note.driver.firstName} ${note.driver.lastName}`
                                      : "-"}
                                  </td>
                                  <td>
                                    <Badge bg="secondary">
                                      {note.driver?.cin || "-"}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg="info">
                                      {note.cities?.length || 0} ville(s)
                                    </Badge>
                                  </td>
                                  <td className="text-right">
                                    {note.totalTTC?.toFixed(2)} TND
                                  </td>
                                  <td className="text-center">
                                    {availableProducts === 0 ? (
                                      <Badge bg="danger">0 disponible</Badge>
                                    ) : (
                                      <Badge bg="success">
                                        {availableProducts} produit(s)
                                      </Badge>
                                    )}
                                  </td>
                                  <td>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedShippingNote({
                                          value: note.id,
                                          label: note.invoiceNumber,
                                        });
                                        loadShippingNoteData(note);
                                      }}
                                      disabled={availableProducts === 0}
                                    >
                                      <i className="fas fa-download"></i>
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      ) : (
                        <p className="text-muted text-center mb-0">
                          {currentUser?.role === "COMMERCIAL"
                            ? "Aucune facture bon sortie trouvée pour ce chauffeur avec votre CIN"
                            : "Aucune facture bon sortie trouvée pour ce chauffeur"}
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {selectedShippingNote && (
              <Row className="mt-3">
                <Col md="12">
                  <Alert variant="success">
                    <i className="fas fa-check-circle mr-2"></i>
                    Facture bon sortie sélectionnée :
                    <strong>{selectedShippingNote.label}</strong>
                    <span className="ml-2">
                      (ID: {selectedShippingNote.value})
                    </span>
                  </Alert>
                </Col>
              </Row>
            )}

            <br />

            {/* Items Table */}
            <Row>
              <Col md="12">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Button variant="success" onClick={handleAddItem}>
                    <i className="fas fa-plus mr-2"></i>
                    Ajouter un article
                  </Button>
                </div>

                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Quantité</th>
                      {shouldShowOriginalQuantity() && <th>Qté originale</th>}
                      <th>Prix unitaire (TND)</th>
                      <th>TVA %</th>
                      <th>Montant TVA</th>
                      <th>Total HT</th>
                      <th>Total TTC</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Select
                            placeholder="Sélectionner un produit"
                            value={getProductOptions().find(
                              (p) => p.value === item.productId,
                            )}
                            options={getProductOptions()}
                            onChange={(selectedOption) => {
                              handleItemChange(
                                index,
                                "productId",
                                selectedOption.value,
                                {
                                  price: selectedOption.price,
                                  vatRate: selectedOption.vatRate,
                                  originalQuantity:
                                    selectedOption.originalQuantity,
                                  shippingNoteItemId:
                                    selectedOption.shippingNoteItemId,
                                },
                              );
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            value={item.quantity || 0}
                            type="number"
                            min="1"
                            max={item.originalQuantity || undefined}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantity",
                                parseInt(e.target.value) || 0,
                              )
                            }
                          />
                        </td>
                        {shouldShowOriginalQuantity() && (
                          <td className="text-center align-middle">
                            {item.originalQuantity ? (
                              <Badge bg="info">{item.originalQuantity}</Badge>
                            ) : (
                              <Badge bg="secondary">-</Badge>
                            )}
                            {item.shippingNoteItemId && (
                              <small className="d-block text-muted">
                                ID: {item.shippingNoteItemId}
                              </small>
                            )}
                          </td>
                        )}
                        <td>
                          <Form.Control
                            value={item.price || 0}
                            type="number"
                            step="0.01"
                            min="0"
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "price",
                                parseFloat(e.target.value) || 0,
                              )
                            }
                            readOnly={selectedShippingNote}
                          />
                        </td>
                        <td>
                          <Form.Control
                            value={item.vatRate}
                            type="number"
                            step="0.01"
                            min="0"
                            readOnly
                          />
                        </td>
                        <td className="text-right">
                          {(item.vatAmount || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {(item.totalHT || 0).toFixed(2)}
                        </td>
                        <td className="text-right">
                          {(item.totalTTC || 0).toFixed(2)}
                        </td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Totals */}
            <Row className="mt-3">
              <Col md="3" className="offset-md-6">
                <Form.Group>
                  <label>Total HT</label>
                  <Form.Control
                    value={(totalHT || 0).toFixed(2)}
                    readOnly
                    className="text-right font-weight-bold"
                  />
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group>
                  <label>Total TVA</label>
                  <Form.Control
                    value={(totalVAT || 0).toFixed(2)}
                    readOnly
                    className="text-right"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md="3" className="offset-md-9">
                <Form.Group>
                  <label>Total TTC</label>
                  <Form.Control
                    value={(totalTTC || 0).toFixed(2)}
                    readOnly
                    className="text-right font-weight-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>

          <Card.Footer>
            <Button
              className="btn-wd btn-outline mr-1 float-left"
              variant="primary"
              onClick={submitForm}
            >
              <i className="fas fa-save mr-2"></i>
              Enregistrer
            </Button>
          </Card.Footer>
        </Card>
      </Form>
    </Container>
  );
}

export default AddDeliveryNote;
